<template>
    <div class="marketing-corp-authentication" :style="customStyle">
        <div class="form-header">
            <div class="main-title">{{ emailAuthConfig.mainTitle }}</div>
            <div class="subtitle">{{ emailAuthConfig.subtitle }}</div>
        </div>
        <form class="form-content">
            <div v-if="!isLogin" class="not-login-mask" @click="checkUser(true)" />
            <div class="form-item">
                <div v-if="showEmailLabel" class="form-item-label">
                    公司邮箱
                </div>
                <div class="form-item-content">
                    <input
                        v-model="formData.email"
                        placeholder="请输入公司邮箱"
                        class="form-item-input"
                        type="text"
                        ref="emailIpt"
                        @focus="handleInputFocus('emailIpt')"
                        @blur="handleEmailInputBlur"
                    />
                    <CommonPicker
                        title="请选择邮箱后缀"
                        placeHolder="请选择"
                        class="form-item-email-suffix"
                        :options="emailSuffixList"
                        :value="formData.emailSuffix"
                        @select="handleSelectEmailSuffix"
                    ></CommonPicker>
                </div>
            </div>
            <div class="form-item">
                <div v-if="showVerificationLabel" class="form-item-label">
                    验证码
                </div>
                <div class="form-item-content">
                    <input
                        v-model="formData.captcha"
                        placeholder="请输入验证码"
                        class="form-item-input"
                        type="text"
                        ref="captchaIpt"
                        @focus="handleInputFocus('captchaIpt')"
                        @blur="handleVerificationInputBlur"
                    />
                    <div
                        class="form-item-btn"
                        :class="{ disabled: isCountingDown }"
                        @click="getVerifyCode"
                    >
                        {{ verifyCodeBtnText }}
                    </div>
                </div>
            </div>
            <div class="submit-btn-wrap">
                <div
                    class="submit-btn"
                    :style="emailAuthConfig.buttonStyle"
                    :class="{ disabled: !canSubmit || isSubmitting }"
                    @click="handleSubmit"
                >
                    {{ emailAuthConfig.buttonText }}
                </div>
            </div>
        </form>
        <NioDialog
            :visible="showDialog"
            title="验证码已发送"
            content="验证码已发送至认证邮箱，请点击邮箱中的认证链接，完成认证。如未收到邮件，请至垃圾箱查找。"
            confirmClass="radius"
            confirmText="我知道了"
            :showCancel="false"
            @confirm="handleCloseDialog"
        />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import CommonPicker from '@/marketing_components/form/picker/common-picker/index.vue';
import Privacy from '@/marketing_components/form/privacy/index.vue';
import NioDialog from '@/marketing_components/common/nio-dialog';
import trackExposeMixin from '../mixin/track-expose';
import loginMixin from "../mixin/login"

const mixin = {
    methods: {
        ...mapActions('marketing/', [
            'getCorpUserAuthenticationDetail',
            'sendCorpUserAuthenticationEmailCaptcha',
            'submitCorpUserAuthentication',
        ])
    }
};

export default {
    name: 'marketing-corp-authentication',
    componentName: 'marketingCorpAuthentication',
    mixins: [mixin, trackExposeMixin, loginMixin],
    components: { CommonPicker, Privacy, NioDialog },
    props: {
        campaignCode: {
            type: String,
            default: ''
        },
        emailAuthConfig: {
            type: Object,
            default: () => ({})
        },
        enterpriseCode: {
            type: String,
            default: ''
        },
        passPage: {
            type: String,
            default: ''
        },
        inactivePage: {
            type: String,
            default: ''
        },
        customStyle: String,
        rootMark: String,
        trackName: String,
        trackSelectAction: Function,
        trackCommonAction: Function,
        componentId: [String, Number],
        positionRank: [String, Number]
    },
    data() {
        return {
            showEmailLabel: false,
            showVerificationLabel: false,
            formData: {
                email: '', // 邮箱
                emailSuffix: '', // 邮箱后缀
                captcha: '' // 验证码
            },
            emailSuffixList: [], // 邮箱后缀列表
            formRules: {
                email: [{ require: true, message: '请输入邮箱' }],
                emailSuffix: [{ require: true, message: '请选择邮箱后缀' }],
                captcha: [{ require: true, message: '请输入邮箱验证码' }]
            },
            countDown: 60,
            isCountingDown: false,
            verifyCodeBtnText: '获取验证码',
            isSubmitting: false,
            showDialog: false
        };
    },
    computed: {
        canSubmit() {
            return this.checkFormContents(false);
        }
    },
    beforeMount() {
        this.trackExposeCustom.tenant_code = this.enterpriseCode;
    },
    async mounted() {
        this.init();
    },
    methods: {
        async init() {
            try {
                // #1 获取登录信息, 未登录则跳转登录
                await this.checkUser(true);

                if (!this.isLogin) {
                    this.$message('登录注册后，才可参与活动');
                    return;
                }

                // #2 获取企业用户配置
                const res = await this.getCorpUserAuthenticationDetail({
                    enterpriseCode: this.enterpriseCode,
                    campaignCode: this.campaignCode,
                    headers: { authorization: this.token }
                });

                if (res.resultCode !== 'success') {
                    throw `${res.displayMsg || res.message} [${res.requestId}]`;
                }

                const {
                    emailSuffixList = [],
                    userAuthenticateStatus = 0,
                    isActive = false
                } =
                    res.data || {};

                // 非活动期间，直接跳转活动结束页
                if (!isActive) {
                    this.inactivePage
                        ? location.replace(this.inactivePage)
                        : this.$message('活动已结束');

                    return;
                }

                // 已认证直接跳转
                if (userAuthenticateStatus === 2) {
                    this.redirectToNextPage();
                    return;
                }

                // 后缀列表/后缀
                this.emailSuffixList = emailSuffixList.map(item => ({
                    value: item,
                    label: item
                }));
                this.formData.emailSuffix =
                    (this.emailSuffixList[0] || {}).value || '';
            } catch (e) {
                console.warn(`[init] error: ${e}`);
                this.$message('出错了，请重试');
            }
        },
        async getVerifyCode() {
            if (this.isCountingDown) {
                return;
            }

            await this.checkUser(true);
            if (!this.isLogin) return;

            this.trackCommonAction('bind_phone', {
                tenant_code: this.enterpriseCode
            });

            let { countDown } = this;
            const { email, emailSuffix } = this.formData;

            if (!email || !emailSuffix) {
                this.$message('请填写完整邮箱');
                return;
            }

            this.sendCorpUserAuthenticationEmailCaptcha({
                emailAddress: `${email}${emailSuffix}`,
                headers: { authorization: this.token }
            })
                .then(res => {
                    if (res.resultCode !== 'success') {
                        throw `${res.displayMsg} [${res.requestId}]`;
                    }

                    this.showDialog = true;
                    this.verifyCodeBtnText = countDown + 's';
                    this.isCountingDown = true;

                    const interval = setInterval(() => {
                        countDown--;
                        if (countDown <= 0) {
                            clearInterval(interval);
                            this.verifyCodeBtnText = '重新获取';
                            this.countDown = 60;
                            this.isCountingDown = false;
                        } else {
                            this.verifyCodeBtnText = countDown + 's';
                            this.isCountingDown = true;
                            this.countDown = countDown;
                        }
                    }, 1000);
                })
                .catch(e => {
                    console.warn(`[getVerifyCode] error: ${e}`);
                    this.$message('获取邮箱验证码失败，请稍后重试');
                });
        },
        handleCloseDialog() {
            this.showDialog = false;
        },
        handleEmailInputBlur() {
            this.showEmailLabel = this.formData.email ? true : false;
            this.handleInputBlur();
        },
        handleVerificationInputBlur() {
            this.showVerificationLabel = this.formData.captcha ? true : false;
            this.handleInputBlur();
        },
        handleInputFocus(key) {
            const targetEl = this.$refs[key];
            this.$emit('inputFocus', this.rootMark, targetEl);
        },
        handleInputBlur() {
            this.$emit('inputBlur', this.rootMark);
        },
        handleSelectEmailSuffix({ value = '' }) {
            this.formData.emailSuffix = value;
        },

        // 提交
        async handleSubmit() {
            if (!this.checkFormContents(true) || this.isSubmitting) {
                return;
            }

            await this.checkUser(true);
            if (!this.isLogin) {
                this.$message('您尚未登录，请在登录后继续操作');
                return;
            }

            this.trackSelectAction({
                tenant_code: this.enterpriseCode
            });
            this.trackCommonAction('begin', {
                tj_component: this.trackName,
                tenant_code: this.enterpriseCode
            });

            const form = this.createForm();

            this.isSubmitting = true;
            this.submitCorpUserAuthentication({
                ...form,
                headers: { authorization: this.token }
            })
                .then(res => {
                    if (res.resultCode !== 'success') {
                        throw res.displayMsg;
                    }
                    this.$message('提交成功');
                    this.redirectToNextPage();
                })
                .catch(e => {
                    const msg = e || '出错了，请重新提交';

                    console.warn(`[handleSubmit] error: ${e}`);
                    this.$message(msg);
                })
                .finally(() => {
                    this.isSubmitting = false;
                });
        },
        createForm() {
            const { captcha, email = '', emailSuffix = '' } = this.formData;

            return {
                campaignCode: this.campaignCode,
                enterpriseCode: this.enterpriseCode,
                captcha,
                emailAddress: `${email}${emailSuffix}`
            };
        },
        redirectToNextPage() {
            const url = this.passPage;

            url && location.replace(url);
        },
        checkFormContents(showToast) {
            return this.formParamCheckIsValid(this.formRules, showToast);
        },
        formParamCheckIsValid(validateRules, showToast) {
            const errors = [];

            Object.keys(validateRules).forEach(key => {
                const rules = validateRules[key];
                const value = this.formData[key];

                rules.forEach(rule => {
                    if (rule.validate) {
                        if (!rule.validate.apply(this)) {
                            errors.push(rule.message);
                        }
                    } else {
                        if (rule.require) {
                            if (['', undefined, null, false].includes(value)) {
                                errors.push(rule.message);
                            }
                        }
                    }
                });
            });
            if (errors.length) {
                if (showToast) {
                    this.$message(errors[0]);
                }
                return false;
            }
            return true;
        }
    }
};
</script>

<style lang="less" scoped>
@import '~src/assets/css/variable.less';

.marketing-corp-authentication {
    .not-login-mask {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        margin: auto;
        background-color: transparent;
        z-index: 10;
    }

    .form-header {
        margin: 30 * @layout375 20 * @layout375;
        display: flex;
        flex-direction: column;

        .main-title {
            color: #050b27;
            font-size: 24 * @layout375;
        }
        .subtitle {
            font-size: 12 * @layout375;
            color: #999dae;
            margin-top: 5 * @layout375;
        }
    }

    .form-content {
        margin: 0 20 * @layout375;
        position: relative;
    }

    .form-item {
        display: flex;
        align-items: center;
        width: 100%;
        padding: 15 * @layout375 0 6 * @layout375;
        border-bottom: 1 * @layout375 solid #dcdee4;
        margin-bottom: 25 * @layout375;

        &:focus-within {
            transition: border-bottom 0.2s ease-out;
            border-bottom: 1 * @layout375 solid #050b27;
        }

        &-label {
            flex-shrink: 0;
            width: 83 * @layout375;
            font-size: 16 * @layout375;
            color: #363c54;
        }
        &-text {
            flex: 1;
            font-size: 16 * @layout375;
            color: #363c54;
        }

        &-content {
            display: flex;
            justify-content: space-between;
            width: 100%;
            align-items: center;
            overflow: hidden;
        }

        &-input {
            font-size: 16 * @layout375;
            color: #363c54;
            height: 22 * @layout375;
            line-height: 22 * @layout375;
            padding: 4 * @layout375 0;
            min-width: 2 * @layout375;
            flex: 1;
            caret-color: #0e999e;
        }

        .input-placeholder {
            color: #cdced4;
        }

        &-email-suffix {
            width: fit-content;
            padding-left: 8 * @layout375;
        }

        &-btn {
            width: 80 * @layout375;
            min-width: fit-content;
            min-height: 25 * @layout375;
            margin-left: 8 * @layout375;
            padding: 4 * @layout375 10 * @layout375;
            border: 1 * @layout375 solid #363c54;
            border-radius: 14 * @layout375;
            box-sizing: border-box;
            display: flex;
            justify-content: center;
            align-items: center;

            font-size: 12 * @layout375;
            color: #363c54;
            background: #fff;

            &.disabled {
                border-color: #dcdee4;
                color: #dcdee4;
                background: transparent !important;
            }
        }
    }
    .submit-btn-wrap {
        padding: 15 * @layout375 0;
    }

    .submit-btn {
        font-size: 16 * @layout375;
        width: 100%;
        min-height: 45 * @layout375;
        padding: 10 * @layout375 20 * @layout375;
        border: 1 * @layout375 solid transparent;
        border-radius: 23 * @layout375;
        display: flex;
        justify-content: center;
        align-items: center;
        text-align: center;

        &.disabled {
            background: #dcdee4 !important;
            color: #ffffff !important;
            border: none;
        }
    }
}
</style>


// WEBPACK FOOTER //
// src/views/marketing/components/corp-authentication.vue